import React, { useMemo, useState } from 'react';
import {
    ResponsiveContainer,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    BarChart,
    Bar,
    LineChart,
    Line,
    ComposedChart,
} from 'recharts';

export type MonthlyRow = {
    month: string; // YYYY-MM

    // Plan (milestone dueDate 기준)
    planClient: number; // 받을 돈 계획 (CLIENT)
    planVendor: number; // 나갈 돈 계획 (SUBCON)

    // Actual (actual payment paidAt/eventDate 기준)
    actualClient: number; // 받은 돈 실적
    actualVendor: number; // 나간 돈 실적

    // Cumulative (derived, existing)
    cumClient: number; // 누적 수령(실적)
    cumVendor: number; // 누적 지급(실적)
    netCum: number; // cumClient - cumVendor (실적 기준)
};

type ChartType = 'MONTHLY' | 'NET' | 'CUMULATIVE' | 'COMBO';

function fmtCompact(n: number) {
    const v = Number.isFinite(n) ? n : 0;
    return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(v);
}
function fmtFull(n: number) {
    const v = Number.isFinite(n) ? n : 0;
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(v);
}

function pill(active: boolean) {
    return [
        'px-3 py-1.5 rounded-full text-xs font-medium transition',
        active ? 'bg-sky-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    ].join(' ');
}

function CustomTooltip({
    active,
    payload,
    label,
}: {
    active?: boolean;
    payload?: any[];
    label?: string;
}) {
    if (!active || !payload?.length) return null;

    // 음수(outflow)일 수 있으니 툴팁에서는 절댓값으로 표시
    const rows = payload
        .filter((p) => p?.value !== undefined && p?.value !== null)
        .map((p) => ({ name: p.name as string, value: Math.abs(Number(p.value) || 0) }));

    return (
        <div className="rounded-xl border bg-white/95 shadow-sm px-3 py-2">
            <div className="text-[11px] text-gray-500 mb-1">{label}</div>
            <div className="space-y-1">
                {rows.map((r, i) => (
                    <div key={i} className="flex items-center justify-between gap-6 text-xs">
                        <span className="text-gray-600">{r.name}</span>
                        <span className="font-semibold tabular-nums">{fmtFull(r.value)}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

/**
 * 색상 규칙:
 * - 수령(In): Blue
 *   - 계획(Plan): 연한 블루
 *   - 실적(Actual): 진한 블루
 * - 지급(Out): Orange
 *   - 계획(Plan): 연한 오렌지
 *   - 실적(Actual): 진한 오렌지
 * - 누적(Net actual): 진한 검정(굵게)
 */
const COLOR = {
    inPlan: '#93c5fd', // blue-300
    inActual: '#2563eb', // blue-600
    outPlan: '#fdba74', // orange-300
    outActual: '#f97316', // orange-500

    cumInPlan: '#60a5fa', // blue-400
    cumInActual: '#1d4ed8', // blue-700
    cumOutPlan: '#fb923c', // orange-400
    cumOutActual: '#ea580c', // orange-600
    cumNetActual: '#0f172a', // slate-900
};

type RowWithPlanCum = MonthlyRow & {
    cumPlanClient: number;
    cumPlanVendor: number;
};

function withPlanCumulative(data: MonthlyRow[]): RowWithPlanCum[] {
    let accIn = 0;
    let accOut = 0;
    return data.map((r) => {
        accIn += Number(r.planClient) || 0;
        accOut += Number(r.planVendor) || 0;
        return { ...r, cumPlanClient: accIn, cumPlanVendor: accOut };
    });
}

function MonthlyInOutChart({ data }: { data: MonthlyRow[] }) {
    // Outflow는 음수로 내려가게 표시
    const view = useMemo(
        () =>
            data.map((r) => ({
                ...r,
                planOutNeg: -Math.abs(r.planVendor || 0),
                actualOutNeg: -Math.abs(r.actualVendor || 0),
            })),
        [data],
    );

    return (
        <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={view} barCategoryGap={18}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtCompact(Number(v))} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />

                    {/* Inflow */}
                    <Bar dataKey="planClient" name="수령 계획" fill={COLOR.inPlan} radius={[8, 8, 0, 0]} />
                    <Bar dataKey="actualClient" name="수령 실적" fill={COLOR.inActual} radius={[8, 8, 0, 0]} />

                    {/* Outflow (negative) */}
                    <Bar dataKey="planOutNeg" name="지급 계획" fill={COLOR.outPlan} radius={[0, 0, 8, 8]} />
                    <Bar dataKey="actualOutNeg" name="지급 실적" fill={COLOR.outActual} radius={[0, 0, 8, 8]} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function NetChart({ data }: { data: MonthlyRow[] }) {
    const view = useMemo(
        () =>
            data.map((r) => ({
                ...r,
                netPlan: (Number(r.planClient) || 0) - (Number(r.planVendor) || 0),
                netActual: (Number(r.actualClient) || 0) - (Number(r.actualVendor) || 0),
            })),
        [data],
    );

    return (
        <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={view} barCategoryGap={18}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtCompact(Number(v))} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />

                    <Bar dataKey="netPlan" name="순현금 계획" fill={COLOR.inPlan} radius={[8, 8, 0, 0]} />
                    <Bar dataKey="netActual" name="순현금 실적" fill={COLOR.inActual} radius={[8, 8, 0, 0]} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function CumulativeChart({ data }: { data: MonthlyRow[] }) {
    const view = useMemo(() => withPlanCumulative(data), [data]);

    return (
        <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={view}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtCompact(Number(v))} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />

                    {/* ✅ A안: 누적에 Plan + Actual 포함
                        - Plan: 점선
                        - Actual: 실선
                        - Net actual: 굵게
                    */}
                    <Line
                        type="monotone"
                        dataKey="cumPlanClient"
                        name="수령 누적 계획"
                        dot={false}
                        stroke={COLOR.cumInPlan}
                        // strokeWidth={2.5}
                        strokeDasharray="6 4"
                    />
                    <Line
                        type="monotone"
                        dataKey="cumClient"
                        name="수령 누적 실적"
                        dot={false}
                        stroke={COLOR.cumInActual}
                        // strokeWidth={3}
                    />

                    <Line
                        type="monotone"
                        dataKey="cumPlanVendor"
                        name="지급 누적 계획"
                        dot={false}
                        stroke={COLOR.cumOutPlan}
                        // strokeWidth={2.5}
                        strokeDasharray="6 4"
                    />
                    <Line
                        type="monotone"
                        dataKey="cumVendor"
                        name="지급 누적 실적"
                        dot={false}
                        stroke={COLOR.cumOutActual}
                        // strokeWidth={3}
                    />

                    <Line
                        type="monotone"
                        dataKey="netCum"
                        name="순현금 누적 실적"
                        dot={false}
                        stroke={COLOR.cumNetActual}
                        // strokeWidth={4}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

function ComboChart({ data }: { data: MonthlyRow[] }) {
    // Bar(In/Out) + 누적(Plan/Actual/Net) 한 화면에
    const view = useMemo(() => {
        const base = data.map((r) => ({
            ...r,
            planOutNeg: -Math.abs(r.planVendor || 0),
            actualOutNeg: -Math.abs(r.actualVendor || 0),
        }));
        return withPlanCumulative(base as any);
    }, [data]);

    return (
        <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={view} barCategoryGap={18}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtCompact(Number(v))} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />

                    {/* Bars (Monthly) */}
                    <Bar dataKey="planClient" name="수령 계획" fill={COLOR.inPlan} radius={[8, 8, 0, 0]} />
                    <Bar dataKey="actualClient" name="수령 실적" fill={COLOR.inActual} radius={[8, 8, 0, 0]} />
                    <Bar dataKey="planOutNeg" name="지급 계획" fill={COLOR.outPlan} radius={[0, 0, 8, 8]} />
                    <Bar dataKey="actualOutNeg" name="지급 실적" fill={COLOR.outActual} radius={[0, 0, 8, 8]} />

                    {/* Lines (Cumulative Plan/Actual/Net) */}
                    <Line
                        type="monotone"
                        dataKey="cumPlanClient"
                        name="수령 누적 계획"
                        dot={false}
                        stroke={COLOR.cumInPlan}
                        strokeWidth={2}
                        strokeDasharray="6 4"
                    />
                    {/* <Line
                        type="monotone"
                        dataKey="cumClient"
                        name="수령 누적 실적"
                        dot={false}
                        stroke={COLOR.cumInActual}
                        strokeWidth={2.5}
                    /> */}
                    <Line
                        type="monotone"
                        dataKey="cumPlanVendor"
                        name="지급 누적 계획"
                        dot={false}
                        stroke={COLOR.cumOutPlan}
                        strokeWidth={2}
                        strokeDasharray="6 4"
                    />
                    {/* <Line
                        type="monotone"
                        dataKey="cumVendor"
                        name="지급 누적 실적"
                        dot={false}
                        stroke={COLOR.cumOutActual}
                        strokeWidth={2.5}
                    /> */}
                    <Line
                        type="monotone"
                        dataKey="netCum"
                        name="순현금 누적 실적"
                        dot={false}
                        stroke={COLOR.cumNetActual}
                        // strokeWidth={3.5}
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
}

export default function CashflowChart({
    data,
    currency,
    title = 'Cashflow',
}: {
    data: MonthlyRow[];
    currency?: string;
    title?: string;
}) {
    const [type, setType] = useState<ChartType>('COMBO');

    const meta = useMemo(() => {
        if (!data?.length) return { from: '—', to: '—' };
        return { from: data[0].month, to: data[data.length - 1].month };
    }, [data]);

    return (
        <section className="rounded-2xl border bg-white overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 border-b flex items-start justify-between gap-3">
                <div>
                    <div className="text-sm font-semibold text-gray-900">{title}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                        {meta.from} → {meta.to}
                        {currency ? <span className="ml-1">· Unit: {currency}</span> : null}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-1">
                        Monthly: 수령/지급 분리 · Net: (수령-지급) · Cumulative: 누적(계획+실적) · Combo: 둘 다
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button type="button" className={pill(type === 'COMBO')} onClick={() => setType('COMBO')}>
                        Combo
                    </button>
                    <button type="button" className={pill(type === 'MONTHLY')} onClick={() => setType('MONTHLY')}>
                        Monthly
                    </button>
                    <button type="button" className={pill(type === 'NET')} onClick={() => setType('NET')}>
                        Net
                    </button>
                    <button
                        type="button"
                        className={pill(type === 'CUMULATIVE')}
                        onClick={() => setType('CUMULATIVE')}
                    >
                        Cumulative
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className="p-4">
                {!data?.length ? (
                    <div className="text-sm text-gray-500 py-10 text-center">표시할 데이터가 없습니다.</div>
                ) : (
                    <>
                        {type === 'MONTHLY' && <MonthlyInOutChart data={data} />}
                        {type === 'COMBO' && <ComboChart data={data} />}
                        {type === 'NET' && <NetChart data={data} />}
                        {type === 'CUMULATIVE' && <CumulativeChart data={data} />}
                    </>
                )}
            </div>
        </section>
    );
}
